import {
  Flex,
  Group,
  Button,
  Menu,
  Text,
  Box,
  ActionIcon,
  Paper,
  Image,
  Loader,
} from '@mantine/core';
import { NavLink, useNavigate } from 'react-router';
import React, { useEffect, useState } from 'react';
import { IconLink, IconDots, IconArrowLeft, IconX, IconUserPlus } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useEvent } from 'src/meetings/hooks/useEvent';
import { useDeleteCalendarEventMutation } from 'src/meetings/hooks/useDeleteCalendarEventMutation';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { EventTimeCard } from 'src/meetings/detail/components/EventTimeCard';
import { EventInfoRow } from 'src/meetings/detail/components/EventInfoRow';
import { MeetingTabs } from 'src/meetings/detail/components/MeetingTabs';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import PageLoader from 'src/UI/loader/PageLoader';
import { SummaryTab } from './tabs/SummaryTab';
import { MeetingNoteTab } from './tabs/MeetingNoteTab';
import { TranscriptTab } from './tabs/TranscriptTab';
import { PreMeetingTab } from './tabs/PreMeetingTab';
import { ComplianceTab } from './tabs/ComplianceTab';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { ActionRow } from 'src/meetings/components/ActionRow';
import CreateClientModal from 'src/clients/CreateClientModal';
import { AddExistingClientModal } from 'src/meetings/detail/components/AddExistingClientModal';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import RecordingProcessingFailureMessage from 'src/meetings/detail/tabs/RecordingProcessingFailureMessage';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { GradientBorderedInfoCard } from 'src/common/components/card/GradientBorderedInfoCard';

type Props = {
  eventId: string;
  onDelete: () => void;
  basePath: string;
  onClose?: () => void;
  tab?: string;
};

export const MeetingDetailsContent = ({ eventId, onDelete, basePath, onClose, tab }: Props) => {
  const { event, isLoading, refetch: refetchEvent } = useEvent(eventId!);
  const [createClientEmail, setCreateClientEmail] = useState<string | undefined>(undefined);
  const [showAddExistingClientModal, setShowAddExistingClientModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const {
    deleteCalendarEvent,
    error: deleteError,
    isLoading: isDeleting,
  } = useDeleteCalendarEventMutation();
  const {
    transcript,
    isLoading: isLoadingTranscript,
    error: transcriptError,
    refetch: refetchTranscript,
  } = useTranscript({
    eventId,
    enabled: !!event?.hasTranscription,
  });
  const { flagEnabledComplianceChecking } = useFlags();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tab) {
      navigate(`${basePath}/summary`, { replace: true });
    }
  }, [tab, eventId, navigate]);

  const handleClientCreation = async () => {
    const { data: updatedEvent } = await refetchEvent();
    if (updatedEvent?.hasTranscription) {
      refetchTranscript();
    }
    navigate(`${basePath}/summary`);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notifications.show({
        title: 'Link copied',
        message: 'Meeting link has been copied to clipboard',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Failed to copy link',
        message: 'Please try again',
        color: 'red',
      });
    }
  };

  const handleDeleteEvent = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCalendarEvent(eventId!);
      setShowDeleteModal(false);
      onDelete();
    } catch {
      // Error is captured by useMutation and displayed in ConfirmDeleteModal
    }
  };

  if (isLoading || isLoadingTranscript) {
    return <PageLoader />;
  }

  if (!event) {
    return <Box>Event not found</Box>;
  }

  if (transcriptError) {
    return <GenericErrorPage error={transcriptError} />;
  }

  const isTranscriptProcessing =
    transcript?.status === 'PROCESSING' || transcript?.summaryStatus === 'IN_PROGRESS';

  const getTabContent = () => {
    if (tab === 'pre-meeting') {
      return <PreMeetingTab event={event} />;
    }
    if (!transcript) {
      return (
        <Text size="sm" c="gray.7">
          {"We haven't received any recording for this meeting yet."}
        </Text>
      );
    }
    if (transcript?.summaryStatus === 'ERROR' || transcript?.status === 'FAILED') {
      return <RecordingProcessingFailureMessage />;
    }

    if (isTranscriptProcessing) {
      return (
        <GradientBorderedInfoCard leftIcon={<Loader size="24px" />}>
          {"Your recording is being transcribed. You'll be able to use this tab once ready."}
        </GradientBorderedInfoCard>
      );
    }

    switch (tab) {
      case 'summary':
        return <SummaryTab event={event} transcript={transcript} />;
      case 'meeting-note':
        return <MeetingNoteTab event={event} />;
      case 'transcript':
        return <TranscriptTab event={event} transcript={transcript} />;
      case 'pre-meeting':
        return <PreMeetingTab event={event} />;
      case 'compliance':
        return flagEnabledComplianceChecking ? (
          <ComplianceTab event={event} />
        ) : (
          <SummaryTab event={event} transcript={transcript} />
        );
      default:
        return <SummaryTab event={event} transcript={transcript} />;
    }
  };

  const handleCreateClient = (emailAddress: string) => {
    setCreateClientEmail(emailAddress);
  };

  const handleCloseCreateClientModal = () => {
    setCreateClientEmail(undefined);
  };

  const getContent = () => {
    const isClientMeeting = event.eventParticipants.some(p => !!p.participant.client);
    if (isClientMeeting) {
      return (
        <>
          <MeetingTabs currentTab={tab} basePath={basePath} />
          <Box mt="xxl">{getTabContent()}</Box>
        </>
      );
    }

    if (event.hasTranscription) {
      return (
        <Box mt="xxl">
          <Paper
            px="md"
            py="md"
            bg={'gray.0'}
            maw={800}
            style={{
              borderRadius: 12,
            }}
          >
            <Flex direction="row" gap={'md'}>
              <Flex direction={'column'} gap={'xs'}>
                <Text fw={FONT_WEIGHTS.semibold}>No client linked to this meeting</Text>
                <Text>
                  {
                    "We've recorded this meeting, but a client needs to be linked in order to generate summaries, meeting notes, transcripts, and compliance reports. Link an existing client or create a new one from the participants below."
                  }
                </Text>
              </Flex>
            </Flex>
          </Paper>

          <Text fw={FONT_WEIGHTS.bold} mt={'xl'} mb={'md'}>
            Participants
          </Text>
          <Flex gap={'sm'} direction="column" maw={800}>
            {event.eventParticipants
              .filter(p => !!p.participant.emailAddress)
              .map(participant => (
                <ActionRow
                  key={participant.participant.id}
                  icon={
                    <Image
                      style={{
                        width: 40,
                        height: 40,
                      }}
                      radius="xl"
                      src={`https://ui-avatars.com/api/?background=0D8ABC&color=fff&name=${participant.participant.emailAddress!.slice(0, 2)}`}
                    />
                  }
                  title={participant.participant.emailAddress!}
                  button={{
                    label: 'Create client',
                    onClick: () => handleCreateClient(participant.participant.emailAddress!),
                    variant: 'primary',
                  }}
                />
              ))}
            <Group gap="md">
              <Button
                variant="secondary"
                leftSection={<IconUserPlus size={16} />}
                onClick={() => setShowAddExistingClientModal(true)}
              >
                Add existing client
              </Button>
              <Text size="sm" c="gray.6">
                Link a client that&apos;s already in Afternoon to this meeting
              </Text>
            </Group>
          </Flex>
        </Box>
      );
    }

    return (
      <Box mt="xxl">
        This meeting does not have transcription, summary, or notes because it was not linked to a
        client.
      </Box>
    );
  };

  return (
    <Box pb="xl">
      {!onClose && (
        <Flex justify="space-between">
          <NavLink to="/meetings">
            <IconArrowLeft />
          </NavLink>
          <Group gap="xs">
            <Button
              variant="secondary"
              rightSection={<IconLink size={16} />}
              onClick={handleCopyLink}
            >
              Copy link
            </Button>
            <Menu position="bottom-end">
              <Menu.Target>
                <Button variant="secondary" px="xs">
                  <IconDots size={16} />
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={handleDeleteEvent}>Delete event</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Flex>
      )}

      <Box mt="sm">
        <Flex justify="space-between">
          <EventTimeCard
            scheduledStartAt={event.scheduledStartAt}
            scheduledEndAt={event.scheduledEndAt}
            startAt={event.startAt}
            endAt={event.endAt}
          />

          {onClose && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            >
              <IconX size={20} />
            </ActionIcon>
          )}
        </Flex>
      </Box>

      <Box mt="xs">
        <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="gray.10">
          {event.title}
        </Text>
      </Box>

      <Box mt="xxs">
        <EventInfoRow event={event} />
      </Box>

      <Box mt="lg">{getContent()}</Box>

      <ConfirmDeleteModal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete event"
        message={`Are you sure you want to delete "${event.title}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />

      {createClientEmail && (
        <CreateClientModal
          opened={true}
          close={handleCloseCreateClientModal}
          emailAddress={createClientEmail}
          navigateAfterCreation={false}
          onClientCreated={handleClientCreation}
        />
      )}

      <AddExistingClientModal
        opened={showAddExistingClientModal}
        onClose={() => setShowAddExistingClientModal(false)}
        eventId={eventId}
        onClientAdded={handleClientCreation}
      />
    </Box>
  );
};
